import { type JSX, useEffect, useState } from 'react';
import { Alert, Group, Loader, Modal, Text } from '@mantine/core';
import { type AtendimentoDetalhe, type Capacidades, api, explicarErro } from '../lib/api.js';
import { DetalheAtendimento } from './DetalheAtendimento.js';

/**
 * A visão de AUDITORIA de um atendimento, aberta de dentro da conversa.
 *
 * Antes era uma seção da navegação ("Atendimentos"), ao lado de "Conversas" —
 * e a navegação não dizia qual das duas escolher, porque as duas mostram o
 * mesmo dado em recortes diferentes. Como link contextual a relação fica
 * explícita: o detalhe é sobre ESTE atendimento, que a pessoa já está lendo.
 *
 * O conteúdo é o mesmo `DetalheAtendimento` de sempre, intacto: passo a passo
 * com nó da árvore, faixa, via, cosseno e candidatos. As rotas de API também
 * não mudaram.
 */
export function DetalheTecnico({
  atendimentoId,
  capacidades,
  aoFechar,
}: {
  atendimentoId: string;
  capacidades: Capacidades;
  aoFechar: () => void;
}): JSX.Element {
  const [detalhe, setDetalhe] = useState<AtendimentoDetalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    void api
      .get<AtendimentoDetalhe>(`/admin/atendimentos/${atendimentoId}`)
      .then((d) => {
        if (vivo) setDetalhe(d);
      })
      .catch((e: unknown) => {
        if (vivo) setErro(explicarErro(e, 'falha ao abrir o atendimento'));
      });
    return () => {
      vivo = false;
    };
  }, [atendimentoId]);

  return (
    <Modal
      opened
      onClose={aoFechar}
      // Tela cheia no celular: a auditoria é densa e não cabe em caixa.
      fullScreen={typeof window !== 'undefined' && window.innerWidth < 768}
      size="xl"
      title="Detalhe técnico do atendimento"
    >
      {erro && (
        <Alert color="red" variant="light" role="alert">
          {erro}
        </Alert>
      )}
      {!detalhe && !erro && (
        <Group gap="xs">
          <Loader size="sm" />
          <Text c="dimmed">Carregando…</Text>
        </Group>
      )}
      {detalhe && (
        <DetalheAtendimento
          atendimento={detalhe}
          capacidades={capacidades}
          aoFechar={aoFechar}
        />
      )}
    </Modal>
  );
}
